import * as React from "react";
import Button from "@mui/material/Button";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import { ChevronDown } from "lucide-react";
import ButtonGroup from "@mui/material/ButtonGroup";
import MenuList from "@mui/material/MenuList";
import { SxProps } from "@mui/material";
import PopperMenu from "./PopperMenu";

export type DropdownButtonOption = {
  label: React.ReactNode;
  selectedTitle?: string;
  value: string;
  onClick?: (value: string) => void;
  disabled?: boolean;
};

export interface DropdownButtonProps {
  disabled?: boolean;
  options: DropdownButtonOption[];
  buttonSubtitle?: React.ReactNode;
  initialValue?: string;
  onSelect?: (value: string) => void;
  onClick: (value: string) => void;
  variant?: "outlined" | "contained";
  size?: "small" | "medium" | "large";
  sx?: SxProps;
  dropdownButtonId?: string;
}

export default ({
  disabled,
  onClick,
  options,
  buttonSubtitle,
  initialValue,
  variant = "outlined",
  size = "small",
  sx,
  dropdownButtonId,
}: DropdownButtonProps) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = React.useState(
    initialValue ?? options[0].value,
  );

  const handleClick = () => {
    onClick(selectedValue);
  };

  const handleMenuItemClick = (option: DropdownButtonOption) => {
    setSelectedValue(option.value);
    setOpen(false);
    option.onClick?.(option.value);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const selected = options.find((option) => option.value === selectedValue);

  return (
    <>
      <ButtonGroup
        size={size}
        variant={variant}
        sx={{
          width: "fit-content",
          padding: 0,
          backgroundColor: "white",
          ...sx,
        }}
        ref={anchorRef}
      >
        <Button
          size={size}
          onClick={handleClick}
          disabled={disabled}
          disableRipple
          sx={{
            borderRight: "none",
            "&:hover": {
              background: "#E8F2F2",
              borderRight: "none",
            },
          }}
          // // Fix the border right color looking doubled on hover
          // borderRightColor: "transparent !important",
        >
          {selected?.selectedTitle}
        </Button>
        <Button
          disableRipple
          disabled={disabled}
          size={size}
          onClick={handleToggle}
          id={dropdownButtonId}
          sx={{
            height: "unset",
            padding: "0 8px 0 6px",
            "&:hover": {
              background: "#E8F2F2",
            },
          }}
        >
          <ChevronDown />
        </Button>
      </ButtonGroup>
      <PopperMenu
        // sx={{ zIndex: 1 }}
        open={open}
        anchorRef={anchorRef}
        handleClose={handleClose}
      >
        <MenuList id="split-button-menu" autoFocusItem>
          {buttonSubtitle && (
            <ListSubheader
              sx={{
                paddingTop: "4px",
                lineHeight: "28px",
                color: "unset",
                fontWeight: 600,
              }}
            >
              {buttonSubtitle}
            </ListSubheader>
          )}
          {options.map((option) => (
            <MenuItem
              sx={{
                backgroundColor: "unset",
                "&.Mui-selected": {
                  backgroundColor: "unset",
                },
                "&:hover": {
                  backgroundColor: "#F9FBFB",
                },
              }}
              key={option.value}
              disabled={option.disabled || disabled}
              selected={option.value === selectedValue}
              onClick={() => handleMenuItemClick(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </PopperMenu>
    </>
  );
};
